<template>
  <div>
    <h1 class="text-2xl font-weight-bold mb-6">Share analytics</h1>
    <div v-if="hasNoAnalytics">
      <p>
        No analytics yet! Please try again after some time.<br />
        If you still can't see them after awhile, please
        <a href="javascript:;" onclick="$crisp.push(['do', 'chat:open']);">contact support.</a>
      </p>
    </div>
    <div
      v-else-if="isAnalyticsDataFetched"
      class="md:flex space-y-3 md:space-x-3 md:space-y-0 bg-block rounded-lg px-4 py-8"
    >
      <div class="w-full md:w-1/2">
        <img class="shadow-lg" :src="imageUrl" />
      </div>
      <div class="md:w-1/2 space-y-3">
        <div class="input-group-alternative textarea-container">
          <div class="tweet-container">
            <div>
              <textarea
                v-model="tweetText"
                ref="textarea"
                placeholder="What would you like to say?"
                maxlength="280"
                class="form-control simple"
                dir="auto"
              ></textarea>
            </div>
          </div>
          <div
            class="thread-preview flex items-center space-x-4 p-3 focus--hide vue-portal-target"
          ></div>
          <div class="thread-actions flex item-center p-1">
            <div class="ml-auto flex items-center space-x-1">
              <div class="character-count" :class="charactersLeftClass">
                {{ charactersLeftCounter }}
              </div>
            </div>
          </div>
        </div>
        <div class="flex space-x-2 items-center justify-end">
          <base-button
            class="btn text primary"
            @click="postNow"
            :disabled="tweetText.length === 0 || isAddingToQueue"
            :loading="isPostingNow"
          >
            Tweet now!
          </base-button>
          <base-button
            class="btn text secondary"
            @click="addToQueue"
            :disabled="tweetText.length === 0 || isPostingNow"
            :loading="isAddingToQueue"
          >
            Add to Queue
          </base-button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  const moment = require('moment-timezone');
  import controller from '@/controller';
  import { countFormatter } from '@/util/countFormatter';
  import { countTweetLength } from '@/../functions/src/util/countTweetLength';
  import { mapGetters, mapState } from 'vuex';
  import storage from '@/storage';
  import SwalModalMixin from './Mixins/SwalModalMixin.vue';
  import { Thread } from '../models/Thread';
  import { removeQueryParamFromURL } from '../util/removeQueryParamFromURL';

  export default {
    name: 'ShareAnalytics',
    computed: {
      ...mapGetters({ currentUser: 'getCurrentUser', userProfile: 'getUserProfile' }),
      ...mapState(['schedule']),
      charactersLeftClass() {
        const statusLength = countTweetLength(this.tweetText);
        if (statusLength >= 280) {
          return 'character-count-exceeded';
        }

        if (statusLength > 260) {
          return 'character-count-warning';
        }

        return 'bg-light';
      },
      charactersLeftCounter() {
        return 280 - countTweetLength(this.tweetText);
      },
      date() {
        const dateFromUrl = moment(this.$route.query.date || this.$route.query.week);
        if (!dateFromUrl.isValid() || dateFromUrl.isAfter(moment())) {
          removeQueryParamFromURL(this.$router, this.$route, 'date');
          return moment();
        }
        return dateFromUrl;
      },
      imageText() {
        if (!this.tweetsAnalytics || !this.followersAnalytics) {
          return '';
        }
        let text =
          `<div style="font-family: Arial; font-size:1.5rem; padding: 0.6rem;">` +
          `<h3 style="color: white; text-align:center">Analytics for @${this.userProfile.username}</h3>`;

        text += this.isWeeksAnalytics
          ? `<div style="padding-top: 1rem; text-align:center">
              ${this.date.clone().subtract(7, 'days').format('ll')} - ${this.date.format('ll')}
            </div>`
          : `<div style="padding-top: 1rem; text-align:center">${this.date.format('ll')}</div>`;

        text +=
          `<p style="padding-left: 10rem">` +
          `<div style="padding-top: 0.4rem">Followers gained: ${countFormatter(
            this.followersAnalytics.followersDiff,
            1
          )}</div><br />` +
          `<div style="padding-top: 1rem">Total followers: ${countFormatter(
            this.followersAnalytics.totalFollowers,
            1
          )}</div><br />` +
          `<div style="padding-top: 1rem">Profile clicks: ${countFormatter(
            this.tweetsAnalytics.userProfileClickCount,
            1
          )}</div><br />` +
          `<div style="padding-top: 1rem">Impressions: ${countFormatter(
            this.tweetsAnalytics.impressionCount,
            1
          )}</div><br />` +
          `</p></div>`;
        return text;
      },
      isWeeksAnalytics() {
        return Boolean(this.$route.query.week);
      },
    },
    mixins: [SwalModalMixin],
    data() {
      return {
        imageUrl: null,
        imageFile: null,
        isAnalyticsDataFetched: false,
        followersAnalytics: null,
        tweetsAnalytics: null,
        tweetText: 'GM',
        isAddingToQueue: false,
        isPostingNow: false,
        hasNoAnalytics: false,
      };
    },
    async mounted() {
      await this.fetchAnalytics();
      this.isAnalyticsDataFetched = true;
      this.createImage();
      this.$nextTick(() => {
        if (this.$refs.textarea) {
          this.$refs.textarea.focus();
        }
      });
    },
    methods: {
      async addToQueue() {
        this.isAddingToQueue = true;
        const thread = await this.createThreadForSaving(false);
        await thread.saveToFirestore(
          this.currentUser,
          this.userProfile.uid,
          this.userProfile.timezone,
          this.$eventStore
        );
        this.$notify({
          type: 'success',
          message: 'Added to Queue',
        });
        this.isAddingToQueue = false;
      },
      async postNow() {
        this.isPostingNow = true;
        const thread = await this.createThreadForSaving(true);
        await thread.saveToFirestore(
          this.currentUser,
          this.userProfile.uid,
          this.userProfile.timezone,
          this.$eventStore
        );
        this.$notify({
          type: 'success',
          message: 'Tweet on its way!',
        });
        this.isPostingNow = false;
      },
      async createThreadForSaving(isToBePostedNow) {
        const mediaItem = await storage
          .uploadNewMedia(this.imageFile)
          .then(({ file, thumbnail }) => {
            const name = file.metadata.name;
            const type = file.metadata.contentType;
            const size = file.metadata.size;

            return {
              name,
              altText: '',
              type,
              size,
              thumbnail,
            };
          })
          .catch((error) => {
            this.swalModal({
              title: 'Error',
              html: `Error uploading media ${this.imageFile.name}`,
            });
            console.error(error);
          });
        const tweets = [
          {
            status: this.tweetText,
            count: 0,
            media: [mediaItem],
          },
        ];
        const time = isToBePostedNow ? new Date() : this.schedule.getNextTimeSlot().toDate();
        return Thread.newThread({
          time,
          tweets,
          user: this.userProfile.uid,
          postNow: isToBePostedNow
        });
      },
      async fetchAnalytics() {
        const analytics = await controller.analytics.getUserAnalytics(
          this.userProfile.uid,
          this.date.format('YYYY-MM-DD'),
          this.isWeeksAnalytics
        );

        if (!analytics.tweetsAnalytics || !analytics.followersAnalytics) {
          if (this.$route.query.date || this.$route.query.week) {
            removeQueryParamFromURL(this.$router, this.$route, 'date');
            removeQueryParamFromURL(this.$router, this.$route, 'week');
            window.location.reload();
          } else {
            this.hasNoAnalytics = true;
            return null;
          }
        }

        this.tweetsAnalytics = {
          impressionCount: analytics.tweetsAnalytics.totalValues.impressions,
          userProfileClickCount: analytics.tweetsAnalytics.totalValues.userProfileClicks,
        };
        this.followersAnalytics = {
          followersDiff: analytics.followersAnalytics.dailyDifference[0],
          totalFollowers: analytics.followersAnalytics.totalGrowth[0],
        };
        return;
      },
      createImage() {
        const canvas = document.createElement('canvas');
        canvas.height = 500;
        canvas.width = 500;
        const context = canvas.getContext('2d');
        const data =
          'data:image/svg+xml;charset=utf-8,' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="' +
          canvas.width +
          '" height="' +
          canvas.height +
          '">' +
          '<foreignObject width="100%" height="100%">' +
          '<div xmlns="http://www.w3.org/1999/xhtml">' +
          this.imageText +
          '</div>' +
          '</foreignObject>' +
          '</svg>';

        const backgroundImage = new Image();
        backgroundImage.src = './img/analytics_bg.png';
        backgroundImage.onload = () => {
          context.drawImage(backgroundImage, 0, 0);
          const image = new Image();
          image.src = data;
          image.onload = async () => {
            context.drawImage(image, 0, 0);
            const blob = await (await fetch(canvas.toDataURL())).blob();
            this.imageFile = new File([blob], 'image.png', { type: 'image/png' });
            this.imageUrl = canvas.toDataURL();
          };
        };
      },
    },
  };
</script>
